    import React, { useState, useEffect } from 'react';
    import {
      Autocomplete,
      Box,
      Container,
      Typography,
      Paper,
      Table,
      TableBody,
      TableCell,
      TableContainer,
      TableHead,
      TableRow,
      TextField,
      Chip,
      IconButton,
      Tooltip,
      Button,
      Dialog,
      DialogTitle,
      DialogContent,
      DialogActions,
      FormControl,
      InputLabel,
      Select,
      MenuItem,
      Alert,
      Checkbox,
      List,
      ListItem,
      ListItemButton,
      ListItemText,
    } from '@mui/material';
    import {
      Search as SearchIcon,
      Edit as EditIcon,
      Delete as DeleteIcon,
      Add as AddIcon,
      Description as DescriptionIcon,
      AssignmentTurnedIn as ApprovementIcon,
    } from '@mui/icons-material';
    import { useNavigate } from 'react-router-dom';
    import Layout from './Layout';
    import { useAuth } from '../contexts/AuthContext';
    import { nominationsAPI, usersAPI, documentsAPI } from '../services/api';

    interface Nomination {
      nominationId: number;
      userId: number;
      nominationName: string;
      vesselName: string;
      clientName: string;
      expectWeight: number | null;
      currentWeight: number;
      containerNumber: number;
      approved?: boolean;
      createdAt: string;
      updatedAt: string;
      user?: {
        userId: number;
        firstName: string;
        lastName: string;
      };
      inspectors?: Array<{
        userId: number;
        firstName: string;
        lastName: string;
      }>;
      containers?: Array<{
        containerId: number;
        number: string;
        bagsNumber: number;
        totalNetWeight: number;
        totalGrossWeight: number;
      }>;
    }

    interface CreateNominationRequest {
      userId: number;
      inspectorIds: number[];
      nominationName: string;
      vesselName: string;
      clientName: string;
      expectWeight: number | null;
      containerNumber: number;
    }

    interface User {
      userId: number;
      firstName: string;
      lastName: string;
      login: string;
      roleId?: number;
    }

    const DEFAULT_CLIENT_NAME = 'WILMAR SUGAR PTE LTD';

    const Nominations: React.FC = () => {
      const navigate = useNavigate();
      const [nominations, setNominations] = useState<Nomination[]>([]);
      const [filteredNominations, setFilteredNominations] = useState<Nomination[]>([]);
      const [isLoading, setIsLoading] = useState(true);
      const [searchTerm, setSearchTerm] = useState('');
      const [isFetching, setIsFetching] = useState(false);

      // Create nomination dialog state
      const [createDialogOpen, setCreateDialogOpen] = useState(false);
      const [users, setUsers] = useState<User[]>([]);
      const [isCreating, setIsCreating] = useState(false);
      const [createError, setCreateError] = useState<string | null>(null);
      const [createSuccess, setCreateSuccess] = useState(false);
      
      // Edit nomination dialog state
      const [editDialogOpen, setEditDialogOpen] = useState(false);
      const [editingNomination, setEditingNomination] = useState<Nomination | null>(null);
      const [isUpdating, setIsUpdating] = useState(false);
      const [editError, setEditError] = useState<string | null>(null);
      const [editSuccess, setEditSuccess] = useState(false);

      // Delete nomination dialog state
      const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
      const [deletingNomination, setDeletingNomination] = useState<Nomination | null>(null);
      const [isDeleting, setIsDeleting] = useState(false);
      const [deleteError, setDeleteError] = useState<string | null>(null);
      
      // Document download state
      const [downloadingDocument, setDownloadingDocument] = useState<number | null>(null);
      const [documentError, setDocumentError] = useState<string | null>(null);

      // Approvement modal (Admin only)
      const [approvementOpen, setApprovementOpen] = useState(false);
      const [unapprovedList, setUnapprovedList] = useState<Nomination[]>([]);
      const [approvementSelected, setApprovementSelected] = useState<number[]>([]);
      const [approvementLoading, setApprovementLoading] = useState(false);
      const [approvementSaving, setApprovementSaving] = useState(false);
      const [approvementError, setApprovementError] = useState<string | null>(null);
      
      const { user: authUser } = useAuth();
      const isAdmin = authUser?.roleName === 'Admin';
      
      const INSPECTOR_ROLE_ID = 3;
      const inspectorsList = users.filter(u => u.roleId === INSPECTOR_ROLE_ID);
      const clientOptions = Array.from(new Set([
        DEFAULT_CLIENT_NAME,
        ...nominations.map(nom => nom.clientName?.trim()).filter((name): name is string => !!name),
      ])).sort((first, second) => first.localeCompare(second));

      // Form state
      const [formData, setFormData] = useState<CreateNominationRequest>({
        userId: 0,
        inspectorIds: [],
        nominationName: '',
        vesselName: '',
        clientName: DEFAULT_CLIENT_NAME,
        expectWeight: null,
        containerNumber: 0,
      });

      useEffect(() => {
        fetchNominations();
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        if (searchTerm.trim() === '') {
          setFilteredNominations(nominations);
        } else {
          const filtered = nominations.filter(nom =>
            nom.nominationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            nom.vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (nom.user && `${nom.user.firstName} ${nom.user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (nom.inspectors?.some(i => `${i.firstName} ${i.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())))
          );
          setFilteredNominations(filtered);
        }
      }, [searchTerm, nominations]);

      const fetchNominations = async () => {
        if (isFetching) {
          return;
        }
        
        try {
          setIsFetching(true);
          setIsLoading(true);
          // Clear existing data before fetching
          setNominations([]);
          setFilteredNominations([]);
          
          const response = await nominationsAPI.getAll();
          const nominationsData = response.data.data;
          setNominations(nominationsData);
          setFilteredNominations(nominationsData);
        } catch (error) {
          // Set empty array on error to prevent crashes
          setNominations([]);
          setFilteredNominations([]);
        } finally {
          setIsLoading(false);
          setIsFetching(false);
        }
      };

      const fetchUsers = async () => {
        try {
          const response = await usersAPI.getAll();
          const usersData = response.data.data;
          setUsers(usersData);
        } catch (error) {
          setUsers([]);
        }
      };

      const handleEditNomination = (nominationId: number) => {
        const nom = nominations.find(w => w.nominationId === nominationId);
        if (nom) {
          setEditingNomination(nom);
          setFormData({
            userId: nom.userId,
            inspectorIds: nom.inspectors?.map(i => i.userId) || [],
            nominationName: nom.nominationName,
            vesselName: nom.vesselName,
            clientName: nom.clientName ?? DEFAULT_CLIENT_NAME,
            expectWeight: nom.expectWeight,
            containerNumber: nom.containerNumber,
          });
          setEditDialogOpen(true);
          setEditError(null);
          setEditSuccess(false);
        }
      };

      const handleDeleteNomination = (nominationId: number) => {
        const nom = nominations.find(w => w.nominationId === nominationId);
        if (nom) {
          setDeletingNomination(nom);
          setDeleteDialogOpen(true);
          setDeleteError(null);
        }
      };

      const handleNominationClick = (nomination: Nomination) => {
        navigate(`/nominations/${nomination.nominationId}`);
      };

      const handleDownloadDocument = async (nominationId: number) => {
        try {
          setDownloadingDocument(nominationId);
          setDocumentError(null);

          const response = await documentsAPI.downloadNominationReport(nominationId);
          
          // Create blob and download
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `nomination-${nominationId}-report.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error: any) {
          setDocumentError(error.response?.data?.message || 'Failed to download document');
        } finally {
          setDownloadingDocument(null);
        }
      };

      const handleConfirmDelete = async () => {
        if (!deletingNomination) return;

        try {
          setIsDeleting(true);
          setDeleteError(null);

          await nominationsAPI.remove(deletingNomination.nominationId);

          // Remove the nomination from the local state
          setNominations(prev => prev.filter(w => w.nominationId !== deletingNomination.nominationId));
          setFilteredNominations(prev => prev.filter(w => w.nominationId !== deletingNomination.nominationId));

          // Close dialog and reset state
          setDeleteDialogOpen(false);
          setDeletingNomination(null);
        } catch (error: any) {
          setDeleteError(error.response?.data?.message || 'Failed to delete nomination');
        } finally {
          setIsDeleting(false);
        }
      };

      const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeletingNomination(null);
        setDeleteError(null);
      };

      const handleCreateNomination = async () => {
        try {
          setIsCreating(true);
          setCreateError(null);
          
          // Validate form data
          if (!formData.userId || !formData.vesselName || formData.containerNumber <= 0) {
            setCreateError('Please fill in all required fields with valid values');
            return;
          }

          if (!formData.clientName.trim() || formData.clientName.trim().length > 255) {
            setCreateError('Enter a client name (up to 255 characters)');
            return;
          }

          if (formData.expectWeight != null && formData.expectWeight < 0) {
            setCreateError('Expected weight cannot be negative');
            return;
          }

          // Create nomination
          const response = await nominationsAPI.create(formData);
          
          if (response.data.status === 'success') {
            setCreateSuccess(true);
            setFormData({
              userId: 0,
              inspectorIds: [],
              nominationName: '',
              vesselName: '',
              clientName: DEFAULT_CLIENT_NAME,
              expectWeight: null,
              containerNumber: 0,
            });
            
            // Refresh nominations list
            await fetchNominations();
            
            // Close dialog after a short delay
            setTimeout(() => {
              setCreateDialogOpen(false);
              setCreateSuccess(false);
            }, 1500);
          }
        } catch (error: any) {
          setCreateError(error.response?.data?.message || 'Failed to create nomination');
        } finally {
          setIsCreating(false);
        }
      };

      const handleUpdateNomination = async () => {
        if (!editingNomination) return;
        
        try {
          setIsUpdating(true);
          setEditError(null);
          
          // Validate form data
          if (!formData.userId || !formData.vesselName || formData.containerNumber <= 0) {
            setEditError('Please fill in all required fields with valid values');
            return;
          }

          if (!formData.clientName.trim() || formData.clientName.trim().length > 255) {
            setEditError('Enter a client name (up to 255 characters)');
            return;
          }

          if (formData.expectWeight != null && formData.expectWeight < 0) {
            setEditError('Expected weight cannot be negative');
            return;
          }

          // Update nomination
          const response = await nominationsAPI.update(editingNomination.nominationId, formData);
          
          if (response.data.status === 'success') {
            setEditSuccess(true);
            
            // Refresh nominations list
            await fetchNominations();
            
            // Close dialog after a short delay
            setTimeout(() => {
              setEditDialogOpen(false);
              setEditSuccess(false);
              setEditingNomination(null);
            }, 1500);
          }
        } catch (error: any) {
          setEditError(error.response?.data?.message || 'Failed to update nomination');
        } finally {
          setIsUpdating(false);
        }
      };

      const handleFormChange = (field: keyof CreateNominationRequest, value: any) => {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      };

      const handleOpenCreateDialog = () => {
        setFormData((prev) => ({
          ...prev,
          userId: authUser?.userId ?? 0,
        }));
        setCreateDialogOpen(true);
        setCreateError(null);
        setCreateSuccess(false);
      };

      const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setCreateError(null);
        setCreateSuccess(false);
        setFormData({
          userId: 0,
          inspectorIds: [],
          nominationName: '',
          vesselName: '',
          clientName: DEFAULT_CLIENT_NAME,
          expectWeight: null,
          containerNumber: 0,
        });
      };

      const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditError(null);
        setEditSuccess(false);
        setEditingNomination(null);
        setFormData({
          userId: 0,
          inspectorIds: [],
          nominationName: '',
          vesselName: '',
          clientName: DEFAULT_CLIENT_NAME,
          expectWeight: null,
          containerNumber: 0,
        });
      };

      const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString();
      };

      const handleOpenApprovement = async () => {
        setApprovementOpen(true);
        setApprovementError(null);
        setApprovementSelected([]);
        setApprovementLoading(true);
        try {
          const res = await nominationsAPI.getUnapproved();
          setUnapprovedList(res.data?.data ?? []);
        } catch (e: any) {
          setApprovementError(e.response?.data?.message || 'Failed to load unapproved nominations');
          setUnapprovedList([]);
        } finally {
          setApprovementLoading(false);
        }
      };

      const handleCloseApprovement = () => {
        setApprovementOpen(false);
        setUnapprovedList([]);
        setApprovementSelected([]);
        setApprovementError(null);
      };

      const handleApprovementToggle = (nominationId: number) => {
        setApprovementSelected(prev =>
          prev.includes(nominationId) ? prev.filter(id => id !== nominationId) : [...prev, nominationId]
        );
      };

      const handleApprovementSelectAll = (checked: boolean) => {
        setApprovementSelected(checked ? unapprovedList.map(n => n.nominationId) : []);
      };

      const handleApprovementSave = async () => {
        if (approvementSelected.length === 0) return;
        setApprovementSaving(true);
        setApprovementError(null);
        try {
          await nominationsAPI.approve(approvementSelected);
          setUnapprovedList(prev => prev.filter(n => !approvementSelected.includes(n.nominationId)));
          setApprovementSelected([]);
          fetchNominations();
          handleCloseApprovement();
        } catch (e: any) {
          setApprovementError(e.response?.data?.message || 'Failed to approve nominations');
        } finally {
          setApprovementSaving(false);
        }
      };

      return (
        <Layout>
          <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
            {/* Page Title */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" component="h1">
                  Nomination Management
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body1" color="text.secondary">
                    Total Nominations: {nominations.length}
                  </Typography>
                  {isAdmin && (
                    <Button
                      variant="outlined"
                      startIcon={<ApprovementIcon />}
                      onClick={handleOpenApprovement}
                      sx={{ borderColor: '#CA4300', color: '#CA4300', '&:hover': { borderColor: '#A03600', color: '#A03600', backgroundColor: 'rgba(202, 67, 0, 0.04)' } }}
                    >
                      APPROVEMENT
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateDialog}
                    sx={{
                      backgroundColor: '#CA4300',
                      '&:hover': {
                        backgroundColor: '#A03600'
                      }
                    }}
                  >
                    CREATE NOMINATION
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Search */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Search Nominations"
                  placeholder="Search by nomination name, vessel name, or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredNominations.length} of {nominations.length} nominations
                </Typography>
              </Box>
            </Paper>

            {/* Document Error Alert */}
            {documentError && (
              <Alert 
                severity="error" 
                onClose={() => setDocumentError(null)}
                sx={{ mb: 2 }}
              >
                {documentError}
              </Alert>
            )}

            {/* Nominations Table */}
            <Paper>
              <TableContainer sx={{ overflowX: 'visible' }}>
                <Table size="small" sx={{ tableLayout: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nomination Name</TableCell>
                      <TableCell>Vessel Name</TableCell>
                      <TableCell>Expected Weight</TableCell>
                      <TableCell>Current Weight</TableCell>
                      <TableCell>Containers</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Inspectors</TableCell>
                      <TableCell>Updated At</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          Loading nominations...
                        </TableCell>
                      </TableRow>
                    ) : filteredNominations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No nominations found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredNominations.map((nom) => (
                        <TableRow 
                          key={nom.nominationId} 
                          hover 
                          onClick={() => handleNominationClick(nom)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(202, 67, 0, 0.04)'
                            }
                          }}
                        >
                          <TableCell>
                            <Chip 
                              label={nom.nominationName} 
                              sx={{ 
                                backgroundColor: '#CA4300', 
                                color: '#ffffff',
                                fontWeight: 'bold'
                              }} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {nom.vesselName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {nom.expectWeight != null ? `${Number(nom.expectWeight).toFixed(2)} kg` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {Number(nom.currentWeight).toFixed(2)} kg
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={nom.containerNumber}
                              sx={{
                                backgroundColor: '#49738b',
                                color: '#ffffff',
                              }}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {nom.user ? `${nom.user.firstName} ${nom.user.lastName}` : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {nom.inspectors?.length ? nom.inspectors.map(i => `${i.firstName} ${i.lastName}`).join(', ') : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(nom.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title={nom.approved ? 'Download Report' : 'Report available after approval'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadDocument(nom.nominationId);
                                    }}
                                    disabled={!nom.approved || downloadingDocument === nom.nominationId}
                                    sx={{ color: nom.approved ? '#1976d2' : undefined }}
                                  >
                                    <DescriptionIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditNomination(nom.nominationId);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteNomination(nom.nominationId);
                                  }}
                                  sx={{ color: '#b91c1c' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* CREATE NOMINATION Dialog */}
            <Dialog 
              open={createDialogOpen} 
              onClose={handleCloseCreateDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Create New Nomination</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                  {createError && (
                    <Alert severity="error">{createError}</Alert>
                  )}
                  {createSuccess && (
                    <Alert severity="success">Nomination created successfully!</Alert>
                  )}
                  
                  <FormControl fullWidth required>
                    <InputLabel>User</InputLabel>
                    <Select
                      value={formData.userId}
                      onChange={(e) => handleFormChange('userId', e.target.value)}
                      label="User"
                    >
                      {users.map((user) => (
                        <MenuItem key={user.userId} value={user.userId}>
                          {user.firstName} {user.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={inspectorsList}
                    value={inspectorsList.filter((user) => formData.inspectorIds.includes(user.userId))}
                    onChange={(_, value) => handleFormChange('inspectorIds', value.map((user) => user.userId))}
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    noOptionsText="No inspectors found"
                    ListboxProps={{
                      sx: {
                        maxHeight: 280,
                        overflowY: 'auto',
                      },
                    }}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={option.userId} {...optionProps}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          {option.firstName} {option.lastName}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Inspectors"
                        placeholder="Search and select inspectors"
                        helperText={`Selected: ${formData.inspectorIds.length}`}
                      />
                    )}
                  />
                  
                  <TextField
                    fullWidth
                    required
                    label="Nomination Name"
                    placeholder="e.g., WF001, WF002"
                    value={formData.nominationName}
                    onChange={(e) => handleFormChange('nominationName', e.target.value)}
                    helperText="Leave empty to auto-generate"
                  />

                  <TextField
                    fullWidth
                    required
                    label="Vessel Name"
                    value={formData.vesselName}
                    onChange={(e) => handleFormChange('vesselName', e.target.value)}
                  />

                  <Autocomplete
                    freeSolo
                    options={clientOptions}
                    value={formData.clientName}
                    inputValue={formData.clientName}
                    onChange={(_, value) => handleFormChange('clientName', value ?? '')}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input' || reason === 'clear') handleFormChange('clientName', value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required label="Client (Principal)" inputProps={{ ...params.inputProps, maxLength: 255 }} />
                    )}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Expected Weight (tons)"
                      value={formData.expectWeight ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleFormChange('expectWeight', v === '' ? null : parseFloat(v) || 0);
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Container Number"
                      value={formData.containerNumber}
                      onChange={(e) => handleFormChange('containerNumber', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={handleCloseCreateDialog}
                  disabled={isCreating}
                  sx={{
                    borderColor: '#CA4300',
                    color: '#CA4300',
                    borderRadius: 1,
                    boxShadow: 2,
                    '&:hover': {
                      borderColor: '#A03600',
                      backgroundColor: 'rgba(202, 67, 0, 0.08)',
                      boxShadow: 2,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateNomination} 
                  variant="contained"
                  disabled={isCreating}
                  sx={{
                    backgroundColor: '#CA4300',
                    '&:hover': {
                      backgroundColor: '#A03600'
                    }
                  }}
                >
                  {isCreating ? 'Creating...' : 'Create Nomination'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Approvement Modal (Admin only) */}
            <Dialog open={approvementOpen} onClose={handleCloseApprovement} maxWidth="sm" fullWidth>
              <DialogTitle>Approvement</DialogTitle>
              <DialogContent>
                {approvementError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApprovementError(null)}>
                    {approvementError}
                  </Alert>
                )}
                {approvementLoading ? (
                  <Typography color="text.secondary">Loading unapproved nominations...</Typography>
                ) : unapprovedList.length === 0 ? (
                  <Typography color="text.secondary">No unapproved nominations.</Typography>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Checkbox
                        checked={approvementSelected.length === unapprovedList.length && unapprovedList.length > 0}
                        indeterminate={approvementSelected.length > 0 && approvementSelected.length < unapprovedList.length}
                        onChange={(_, checked) => handleApprovementSelectAll(checked)}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Select all ({unapprovedList.length})
                      </Typography>
                    </Box>
                    <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
                      {unapprovedList.map((nom) => (
                        <ListItem key={nom.nominationId} disablePadding>
                          <ListItemButton onClick={() => handleApprovementToggle(nom.nominationId)} dense>
                            <Checkbox
                              checked={approvementSelected.includes(nom.nominationId)}
                              disableRipple
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <ListItemText
                              primary={nom.nominationName}
                              secondary={nom.vesselName}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={handleCloseApprovement}
                  sx={{
                    borderColor: '#CA4300',
                    color: '#CA4300',
                    borderRadius: 1,
                    boxShadow: 2,
                    '&:hover': {
                      borderColor: '#A03600',
                      backgroundColor: 'rgba(202, 67, 0, 0.08)',
                      boxShadow: 2,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApprovementSave}
                  disabled={approvementSelected.length === 0 || approvementSaving}
                  sx={{ backgroundColor: '#CA4300', '&:hover': { backgroundColor: '#A03600' } }}
                >
                  {approvementSaving ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Edit Nomination Dialog */}
            <Dialog 
              open={editDialogOpen} 
              onClose={handleCloseEditDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Edit Nomination</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                  {editError && (
                    <Alert severity="error">{editError}</Alert>
                  )}
                  {editSuccess && (
                    <Alert severity="success">Nomination updated successfully!</Alert>
                  )}
                  
                  <FormControl fullWidth required>
                    <InputLabel>User</InputLabel>
                    <Select
                      value={formData.userId}
                      onChange={(e) => handleFormChange('userId', e.target.value)}
                      label="User"
                    >
                      {users.map((user) => (
                        <MenuItem key={user.userId} value={user.userId}>
                          {user.firstName} {user.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={inspectorsList}
                    value={inspectorsList.filter((user) => formData.inspectorIds.includes(user.userId))}
                    onChange={(_, value) => handleFormChange('inspectorIds', value.map((user) => user.userId))}
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                    noOptionsText="No inspectors found"
                    ListboxProps={{
                      sx: {
                        maxHeight: 280,
                        overflowY: 'auto',
                      },
                    }}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={option.userId} {...optionProps}>
                          <Checkbox checked={selected} sx={{ mr: 1 }} />
                          {option.firstName} {option.lastName}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Inspectors"
                        placeholder="Search and select inspectors"
                        helperText={`Selected: ${formData.inspectorIds.length}`}
                      />
                    )}
                  />
                  
                  <TextField
                    fullWidth
                    required
                    label="Nomination Name"
                    placeholder="e.g., WF001, WF002"
                    value={formData.nominationName}
                    onChange={(e) => handleFormChange('nominationName', e.target.value)}
                  />

                  <TextField
                    fullWidth
                    required
                    label="Vessel Name"
                    value={formData.vesselName}
                    onChange={(e) => handleFormChange('vesselName', e.target.value)}
                  />

                  <Autocomplete
                    freeSolo
                    options={clientOptions}
                    value={formData.clientName}
                    inputValue={formData.clientName}
                    onChange={(_, value) => handleFormChange('clientName', value ?? '')}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input' || reason === 'clear') handleFormChange('clientName', value);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required label="Client (Principal)" inputProps={{ ...params.inputProps, maxLength: 255 }} />
                    )}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Expected Weight (tons)"
                      value={formData.expectWeight ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleFormChange('expectWeight', v === '' ? null : parseFloat(v) || 0);
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Container Number"
                      value={formData.containerNumber}
                      onChange={(e) => handleFormChange('containerNumber', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={handleCloseEditDialog}
                  disabled={isUpdating}
                  sx={{
                    borderColor: '#CA4300',
                    color: '#CA4300',
                    borderRadius: 1,
                    boxShadow: 2,
                    '&:hover': {
                      borderColor: '#A03600',
                      backgroundColor: 'rgba(202, 67, 0, 0.08)',
                      boxShadow: 2,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateNomination} 
                  variant="contained"
                  disabled={isUpdating}
                  sx={{
                    backgroundColor: '#CA4300',
                    '&:hover': {
                      backgroundColor: '#A03600'
                    }
                  }}
                >
                  {isUpdating ? 'Updating...' : 'Update Nomination'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Nomination Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
              <DialogTitle>Delete Nomination</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {deleteError && (
                    <Alert severity="error">{deleteError}</Alert>
                  )}
                  <Typography variant="body1">
                    Are you sure you want to delete the nomination "{deletingNomination?.vesselName}"?
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={handleCloseDeleteDialog}
                  disabled={isDeleting}
                  sx={{
                    borderColor: '#CA4300',
                    color: '#CA4300',
                    borderRadius: 1,
                    boxShadow: 2,
                    '&:hover': {
                      borderColor: '#A03600',
                      backgroundColor: 'rgba(202, 67, 0, 0.08)',
                      boxShadow: 2,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmDelete} 
                  variant="contained" 
                  disabled={isDeleting}
                  sx={{ 
    backgroundColor: '#b91c1c',
                    '&:hover': { backgroundColor: '#991b1b' }
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Nomination'}
                </Button>
              </DialogActions>
            </Dialog>

          </Container>
        </Layout>
      );
    };

    export { Nominations };
    export default Nominations;
